import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { trackPageView } from "./lib/analytics";
import { AuthProvider } from "./context/AuthContext";
import { PageContentProvider } from "./context/PageContentContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import MaintenanceGate from "./components/MaintenanceGate";
import EditThisPageButton from "./components/EditThisPageButton";
import DocumentTitle from "./components/DocumentTitle";
import Home from "./pages/Home";
import NewsPage from "./pages/NewsPage";
import VideosPage from "./pages/VideosPage";
import StatsPage from "./pages/StatsPage";
import ApiFootballStatsPage from "./pages/ApiFootballStatsPage";
import LineupBuilderPage from "./pages/LineupBuilderPage";
import ClassicLineupBuilderPage from "./pages/ClassicLineupBuilderPage";
import AnalyticsExcludePage from "./pages/AnalyticsExcludePage";
import SocialPage from "./pages/SocialPage";
import SpotifyPage from "./pages/SpotifyPage";
import BlastPage from "./pages/BlastPage";
import CustomPage from "./pages/CustomPage";
import ArchivePage from "./pages/ArchivePage";
import ContactPage from "./pages/ContactPage";
import PostDetail from "./pages/PostDetail";
import ShortLinkResolver from "./pages/ShortLinkResolver";
import Forum from "./pages/Forum";
import ForumMagic from "./pages/ForumMagic";
import AdminForum from "./pages/AdminForum";
import LoginPage from "./pages/Login";
import Admin from "./pages/Admin";
import AdminSocial from "./pages/AdminSocial";
import AdminInbox from "./pages/AdminInbox";
import AdminNewsSources from "./pages/AdminNewsSources";
import AdminVideoChannels from "./pages/AdminVideoChannels";
import AdminPageContent from "./pages/AdminPageContent";
import AdminTheme from "./pages/AdminTheme";
import AdminHomepageWidgets from "./pages/AdminHomepageWidgets";
import AdminHomepageStrip from "./pages/AdminHomepageStrip";
import AdminCustomPages from "./pages/AdminCustomPages";
import AdminSpotify from "./pages/AdminSpotify";
import AdminPageTiles from "./pages/AdminPageTiles";
import AdminExploreRotation from "./pages/AdminExploreRotation";
import AdminPostTypes from "./pages/AdminPostTypes";
import AdminPostTemplates from "./pages/AdminPostTemplates";import AdminNavItems from "./pages/AdminNavItems";
import AdminFooterLinks from "./pages/AdminFooterLinks";
import AdminSquad from "./pages/AdminSquad";
import AdminClassicSquad from "./pages/AdminClassicSquad";
import AdminClassicShirts from "./pages/AdminClassicShirts";
import AdminClassicLineupPresets from "./pages/AdminClassicLineupPresets";
import AdminPolls from "./pages/AdminPolls";
import AdminBackup from "./pages/AdminBackup";
import AdminCarousels from "./pages/AdminCarousels";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminAnalyticsV2 from "./pages/AdminAnalyticsV2";
import AdminProducts from "./pages/AdminProducts";
import AdminInkShop from "./pages/AdminInkShop";
import AdminInkOrders from "./pages/AdminInkOrders";
import InkShopPage from "./pages/InkShopPage";
import InkShopDetail from "./pages/InkShopDetail";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import AdminProductCategories from "./pages/AdminProductCategories";
import AdminTodos from "./pages/AdminTodos";
import AdminMedia from "./pages/AdminMedia";
import AdminComments from "./pages/AdminComments";
import GalleryPage from "./pages/GalleryPage";
import PostEditor from "./pages/PostEditor";
import AuthCallback from "./pages/AuthCallback";
import AnalyticsTracker from "./components/AnalyticsTracker";
import CanonicalUrl from "./components/CanonicalUrl";
import { CommenterProvider } from "./context/CommenterContext";
import { Toaster } from "./components/ui/sonner";

// Wraps any public page in the maintenance gate
const Public = ({ element }) => (
  <MaintenanceGate>
    {element}
    <EditThisPageButton />
  </MaintenanceGate>
);

// Simple scroll-to-top on route navigation so page transitions feel native
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <ThemeProvider>
              <PageContentProvider>
                <CommenterProvider>
                  <DocumentTitle />
                  <CanonicalUrl />
                  <AnalyticsTracker />
                  <Toaster position="bottom-center" richColors closeButton />
                  <AuthRouter />
                </CommenterProvider>
              </PageContentProvider>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

/**
 * Inspects the URL fragment FIRST so we can intercept Emergent's
 * `#session_id=...` redirect before the rest of the routing tree
 * mounts. Per the auth playbook, this must be synchronous, not in a
 * useEffect.
 */
function AuthRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            {/* Public site (hidden behind maintenance gate) */}
            <Route path="/" element={<Public element={<Home />} />} />
            <Route path="/news" element={<Public element={<NewsPage />} />} />
            <Route path="/videos" element={<Public element={<VideosPage />} />} />
            <Route path="/stats" element={<Public element={<StatsPage />} />} />
            <Route path="/football-stats" element={<Public element={<ApiFootballStatsPage />} />} />
            <Route path="/lineup-builder" element={<Public element={<LineupBuilderPage />} />} />
            <Route path="/lineup-builder/:code" element={<Public element={<LineupBuilderPage />} />} />
            <Route path="/classic-lineup-builder" element={<Public element={<ClassicLineupBuilderPage />} />} />
            <Route path="/classic-lineup-builder/:code" element={<Public element={<ClassicLineupBuilderPage />} />} />
            <Route path="/analytics-exclude" element={<Public element={<AnalyticsExcludePage />} />} />
            <Route path="/social" element={<Public element={<SocialPage />} />} />
            <Route path="/spotify" element={<Public element={<SpotifyPage />} />} />
            <Route path="/blast-from-the-past" element={<Public element={<BlastPage />} />} />
            <Route path="/page/:slug" element={<Public element={<CustomPage />} />} />
            <Route path="/gallery/:slug" element={<Public element={<GalleryPage />} />} />
            <Route path="/archive" element={<Public element={<ArchivePage />} />} />
            <Route path="/contact" element={<Public element={<ContactPage />} />} />
            {/* Pre-CMS legacy URLs — the real content now lives at
                /page/{slug}; these just forward old bookmarks/links so
                there's only ever one canonical URL per page. */}
            <Route path="/legal" element={<Navigate to="/page/legal" replace />} />
            <Route path="/privacy" element={<Navigate to="/page/privacy" replace />} />
            <Route path="/audio" element={<Navigate to="/page/audio" replace />} />
            <Route path="/official" element={<Navigate to="/page/official" replace />} />
            <Route path="/post/:id" element={<Public element={<PostDetail />} />} />
            <Route path="/s/:code" element={<Public element={<ShortLinkResolver />} />} />
            <Route path="/thelastpost" element={<Public element={<Forum />} />} />
            <Route path="/thelastpost/magic" element={<Public element={<ForumMagic />} />} />
            {/* Back-compat: keep old /forum URLs working for bookmarks & emails */}
            <Route path="/forum" element={<Navigate to="/thelastpost" replace />} />
            <Route path="/forum/magic" element={<Navigate to="/thelastpost/magic" replace />} />

            {/* Admin (always accessible) */}
            <Route path="/admin/login" element={<LoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/posts/new"
              element={
                <ProtectedRoute>
                  <PostEditor mode="new" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/posts/:id/edit"
              element={
                <ProtectedRoute>
                  <PostEditor mode="edit" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/social"
              element={
                <ProtectedRoute>
                  <AdminSocial />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/forum"
              element={
                <ProtectedRoute>
                  <AdminForum />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inbox"
              element={
                <ProtectedRoute>
                  <AdminInbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/news-sources"
              element={
                <ProtectedRoute>
                  <AdminNewsSources />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/video-channels"
              element={
                <ProtectedRoute>
                  <AdminVideoChannels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/page-content"
              element={
                <ProtectedRoute>
                  <AdminPageContent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/theme"
              element={
                <ProtectedRoute>
                  <AdminTheme />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/homepage-widgets"
              element={
                <ProtectedRoute>
                  <AdminHomepageWidgets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/homepage-strip"
              element={
                <ProtectedRoute>
                  <AdminHomepageStrip />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/spotify"
              element={
                <ProtectedRoute>
                  <AdminSpotify />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/custom-pages"
              element={
                <ProtectedRoute>
                  <AdminCustomPages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/page-tiles"
              element={
                <ProtectedRoute>
                  <AdminPageTiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/explore-rotation"
              element={
                <ProtectedRoute>
                  <AdminExploreRotation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/post-types"
              element={
                <ProtectedRoute>
                  <AdminPostTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/post-templates"
              element={
                <ProtectedRoute>
                  <AdminPostTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/nav"
              element={
                <ProtectedRoute>
                  <AdminNavItems />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/footer-links"
              element={
                <ProtectedRoute>
                  <AdminFooterLinks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/squad"
              element={
                <ProtectedRoute>
                  <AdminSquad />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/classic-squad"
              element={
                <ProtectedRoute>
                  <AdminClassicSquad />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/classic-shirts"
              element={
                <ProtectedRoute>
                  <AdminClassicShirts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/classic-lineup-presets"
              element={
                <ProtectedRoute>
                  <AdminClassicLineupPresets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/polls"
              element={
                <ProtectedRoute>
                  <AdminPolls />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/backup"
              element={
                <ProtectedRoute>
                  <AdminBackup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/carousels"
              element={
                <ProtectedRoute>
                  <AdminCarousels />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics-v2"
              element={
                <ProtectedRoute>
                  <AdminAnalyticsV2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute>
                  <AdminProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ink-shop"
              element={
                <ProtectedRoute>
                  <AdminInkShop />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ink-orders"
              element={
                <ProtectedRoute>
                  <AdminInkOrders />
                </ProtectedRoute>
              }
            />
            <Route path="/shop" element={<Public element={<InkShopPage />} />} />
            <Route path="/shop/:id" element={<Public element={<InkShopDetail />} />} />
            <Route path="/cart" element={<Public element={<CartPage />} />} />
            <Route path="/checkout" element={<Public element={<CheckoutPage />} />} />
            <Route path="/shop/thanks" element={<Public element={<CheckoutSuccessPage />} />} />
            <Route
              path="/admin/product-categories"
              element={
                <ProtectedRoute>
                  <AdminProductCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/todos"
              element={
                <ProtectedRoute>
                  <AdminTodos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/media"
              element={
                <ProtectedRoute>
                  <AdminMedia />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/comments"
              element={
                <ProtectedRoute>
                  <AdminComments />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
